// src/Services/coinService.js
import { doc, updateDoc, getDoc, increment, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import authService from "./authService";

class CoinService {
  // Coin packages with Nigerian pricing
  static COIN_PACKAGES = [
    { coins: 100, price: 1000, popular: false },
    { coins: 280, price: 2500, popular: false },
    { coins: 500, price: 4000, popular: true },
    { coins: 1000, price: 7000, popular: false }
  ];

  // Room upgrade costs
  static UPGRADE_COSTS = {
    1: 100,    // +1 player = 100 coins  → 100-coin pack
    3: 280,    // +3 players = 280 coins → 280-coin pack
    5: 500,    // +5 players = 500 coins → 500-coin pack
    10: 1000   // +10 players = 1000 coins → 1000-coin pack
  };

  // Write isPro: true directly to Firestore after a successful subscription payment.
  // Acts as a reliable fallback in case the Paystack webhook doesn't fire.
  async grantProInFirestore(proType = 'monthly') {
    try {
      const userId = await authService.getCurrentUserId();
      const userRef = doc(db, "users", userId);
      const proExpiry = new Date(Date.now() + 31 * 24 * 60 * 60 * 1000); // ~1 month
      await updateDoc(userRef, {
        isPro: true,
        proExpiry,
        proType,
        proStartedAt: new Date(),
      });
      console.log("✅ Pro granted in Firestore (client-side fallback)");
      return { success: true, proExpiry, proType };
    } catch (error) {
      console.error("Error granting Pro in Firestore:", error);
      return { success: false, error: error.message };
    }
  }

  // Get user's current coin balance
  async getUserCoinBalance() {
    try {
      const userId = await authService.getExistingUserId();
      if (!userId) return 0; // visitor: no account yet
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        return userSnap.data().coinBalance || 0;
      }

      // Create user document if it doesn't exist
      await authService.createUserDocument(userId);
      return 0;
    } catch (error) {
      console.error("Error getting coin balance:", error);
      return 0;
    }
  }

  // Returns coinBalance + Pro subscription status in one read
  async getUserProfile() {
    try {
      const userId = await authService.getExistingUserId();
      if (!userId) return { coinBalance: 0, isPro: false, proExpiry: null }; // visitor: no account yet
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        await authService.createUserDocument(userId);
        return { coinBalance: 0, isPro: false, proExpiry: null };
      }

      const data = userSnap.data();
      const coinBalance = data.coinBalance || 0;
      const isPro = data.isPro === true;
      const proExpiry = data.proExpiry?.toDate?.() || null;
      const proType = data.proType || 'monthly';

      // Treat as not Pro if expiry has passed
      const activelyPro = isPro && (!proExpiry || proExpiry > new Date());

      return { coinBalance, isPro: activelyPro, proExpiry, proType };
    } catch (error) {
      console.error("Error getting user profile:", error);
      return { coinBalance: 0, isPro: false, proExpiry: null };
    }
  }

  // Add coins to user account (after successful purchase)
  async addCoins(amount, purchaseDetails = {}) {
    try {
      const userId = await authService.getCurrentUserId();
      const userRef = doc(db, "users", userId);

      const purchaseRecord = {
        coins: amount,
        price: purchaseDetails.price || 0,
        timestamp: new Date(),
        paymentId: purchaseDetails.paymentId || null,
        package: purchaseDetails.package || null
      };

      await updateDoc(userRef, {
        coinBalance: increment(amount),
        coinPurchaseHistory: arrayUnion(purchaseRecord),
        lastPurchaseAt: new Date()
      });

      console.log(`✅ Added ${amount} coins to user account`);
      return { success: true, newBalance: await this.getUserCoinBalance() };
    } catch (error) {
      console.error("Error adding coins:", error);
      return { success: false, error: error.message };
    }
  }

  // Spend coins for room upgrades
  async spendCoins(amount, purpose = 'room_upgrade', roomId = null) {
    try {
      const userId = await authService.getCurrentUserId();
      const userRef = doc(db, "users", userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        return { success: false, error: 'User document not found' };
      }

      const userData = userDoc.data();
      const currentBalance = userData.coinBalance || 0;

      if (currentBalance < amount) {
        return {
          success: false,
          error: 'insufficient_coins',
          message: `Not enough coins. You have ${currentBalance}, need ${amount}.`
        };
      }

      const newBalance = currentBalance - amount;
      const totalSpent = (userData.totalCoinsSpent || 0) + amount;

      // Create spending record
      const spendingRecord = {
        amount,
        purpose,
        roomId,
        timestamp: new Date()
      };

      // Update user document with new balance, total spent, and spending history
      await updateDoc(userRef, {
        coinBalance: newBalance,
        totalCoinsSpent: totalSpent,
        coinSpendingHistory: arrayUnion(spendingRecord)
      });

      console.log(`💸 Spent ${amount} coins for ${purpose}. New balance: ${newBalance}`);

      return {
        success: true,
        newBalance,
        totalSpent,
        spendingRecord
      };
    } catch (error) {
      console.error('❌ Error spending coins:', error);
      return { success: false, error: error.message };
    }
  }

  // Grant a room upgrade credit (3 sessions, 8-day expiry) after spending coins
  async grantRoomUpgradeCredit(maxPlayers, sessionsRemaining = 3) {
    try {
      const userId = await authService.getCurrentUserId();
      const userRef = doc(db, "users", userId);
      const expiresAt = new Date(Date.now() + 8 * 24 * 60 * 60 * 1000);
      await updateDoc(userRef, {
        roomUpgradeCredit: { maxPlayers, sessionsRemaining, expiresAt }
      });
      return { success: true };
    } catch (error) {
      console.error("Error granting room upgrade credit:", error);
      return { success: false, error: error.message };
    }
  }

  // Check if user can afford a room upgrade
  async canAffordUpgrade(playerIncrease) {
    const cost = this.UPGRADE_COSTS[playerIncrease];
    const balance = await this.getUserCoinBalance();

    return {
      canAfford: balance >= cost,
      cost: cost,
      balance: balance,
      needed: Math.max(0, cost - balance)
    };
  }

  // Get suggested coin package for a specific upgrade
  getSuggestedPackage(coinsNeeded) {
    // Find the smallest package that covers the needed coins
    return this.COIN_PACKAGES.find(pkg => pkg.coins >= coinsNeeded) || this.COIN_PACKAGES[0];
  }

  // Calculate room upgrade cost
  getRoomUpgradeCost(fromPlayers, toPlayers) {
    const increase = toPlayers - fromPlayers;
    return this.UPGRADE_COSTS[increase] || null;
  }
}

export const coinService = new CoinService();
export default coinService;